import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Login from './pages/Login';
import RegisterSchool from './pages/RegisterSchool';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import Staff from './pages/Staff';
import Dispersals from './pages/Dispersals';
import Settings from './pages/Settings';
import StaffRolePermissions from './pages/StaffRolePermissions';
import Classes from './pages/Classes';
import Notices from './pages/Notices';
import Notifications from './pages/Notifications';
import Meetings from './pages/Meetings';
import Payments from './pages/Payments';
import SchoolProfile from './pages/SchoolProfile';
import { authService, ErrorBoundary } from '@uniqcamp/shared';
import { canAccessStaffPortal, canAccessPrincipalFeatures } from './utils/roles';
import { useFeatureAccess } from './hooks/useFeatureAccess';
import { useStaffPermissionContext } from './hooks/useStaffPermissionContext';
import { ToastProvider } from './contexts/ToastContext';

const theme = createTheme({
  palette: {
    primary: { main: '#4f227d', light: '#7b4da8', dark: '#371755', contrastText: '#fff' },
    secondary: { main: '#ffe200', light: '#ffeb3d', dark: '#e6cb00', contrastText: '#1a1a1a' },
    background: { default: '#f8f6fa', paper: '#ffffff' },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  shape: { borderRadius: 0 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { minHeight: 44, textTransform: 'none', fontWeight: 600, '@media (min-width: 600px)': { minHeight: 36 } },
      },
    },
    MuiIconButton: { styleOverrides: { root: { minWidth: 48, minHeight: 48 } } },
    MuiPaper: { styleOverrides: { root: { boxShadow: '0 2px 12px rgba(79, 34, 125, 0.08)' } } },
    MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
    MuiChip: { styleOverrides: { root: {} } },
    MuiAppBar: {
      styleOverrides: {
        root: { background: 'linear-gradient(135deg, #4f227d 0%, #371755 100%)', boxShadow: '0 2px 12px rgba(79, 34, 125, 0.25)' },
      },
    },
    MuiBottomNavigation: { styleOverrides: { root: { backgroundColor: '#fff', boxShadow: '0 -2px 12px rgba(79, 34, 125, 0.08)' } } },
  },
});

// Any staff role (Teacher, Principal, Admin, etc.)
const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = authService.isAuthenticated();
  const user = authService.getStoredUser();

  if (!isAuthenticated || !user || !canAccessStaffPortal(user.role)) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Principal/Admin OR has view/edit for the feature - so Teachers with permission can access
const PrincipalOrFeatureRoute = ({ featureId, children }: { featureId: string; children: React.ReactNode }) => {
  const user = authService.getStoredUser();
  const isPrincipal = user && canAccessPrincipalFeatures(user.role);
  if (isPrincipal) return <>{children}</>;
  // Non-principal: check feature access (loaded in Layout; we need to check same data)
  return <FeatureAccessRoute featureId={featureId}>{children}</FeatureAccessRoute>;
};

const FeatureAccessRoute = ({ featureId, children }: { featureId: string; children: React.ReactNode }) => {
  const { canView, canEdit, loading } = useFeatureAccess(featureId);
  if (loading) return null;
  if (canView || canEdit) return <>{children}</>;
  return <Navigate to="/" replace />;
};

/** Staff section: same aggregate rule as drawer Staff item ({@link hasStaffAreaAggregateAccess}). */
const StaffAreaRoute = ({ children }: { children: React.ReactNode }) => {
  const ctx = useStaffPermissionContext();
  if (ctx.loading) return null;
  if (ctx.isPrincipal) return <>{children}</>;
  if (ctx.hasStaffArea()) return <>{children}</>;
  return <Navigate to="/" replace />;
};

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<RegisterSchool />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="dispersals" element={<Dispersals />} />
            <Route path="dispersals/early" element={<Dispersals />} />
            <Route path="students/relations" element={<Students />} />
            <Route path="students/dispersal" element={<Students />} />
            <Route path="students" element={<Students />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="meetings" element={<Meetings />} />
            <Route path="staff/subjects" element={<StaffAreaRoute><Staff /></StaffAreaRoute>} />
            <Route path="staff/roles" element={<StaffAreaRoute><Staff /></StaffAreaRoute>} />
            <Route path="staff/authority-levels" element={<StaffAreaRoute><Staff /></StaffAreaRoute>} />
            <Route path="staff" element={<StaffAreaRoute><Staff /></StaffAreaRoute>} />
            <Route
              path="classes"
              element={
                <PrincipalOrFeatureRoute featureId="classes">
                  <Classes />
                </PrincipalOrFeatureRoute>
              }
            />
            <Route
              path="notices"
              element={
                <PrincipalOrFeatureRoute featureId="notices">
                  <Notices />
                </PrincipalOrFeatureRoute>
              }
            />
            <Route
              path="school"
              element={
                <PrincipalOrFeatureRoute featureId="school">
                  <Settings standaloneTab="geofence" />
                </PrincipalOrFeatureRoute>
              }
            />
            <Route
              path="gates"
              element={
                <PrincipalOrFeatureRoute featureId="gates">
                  <Settings standaloneTab="gates" />
                </PrincipalOrFeatureRoute>
              }
            />
            <Route
              path="staff-role-permissions"
              element={
                <PrincipalOrFeatureRoute featureId="staffRolePermissions">
                  <StaffRolePermissions />
                </PrincipalOrFeatureRoute>
              }
            />
            <Route
              path="settings/*"
              element={
                <PrincipalOrFeatureRoute featureId="settings">
                  <Settings />
                </PrincipalOrFeatureRoute>
              }
            />
            <Route
              path="payments"
              element={
                <PrincipalOrFeatureRoute featureId="payments">
                  <Payments />
                </PrincipalOrFeatureRoute>
              }
            />
            <Route path="school-profile" element={<SchoolProfile />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      </ToastProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
